import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    DndContext, DragOverlay, useDraggable, useDroppable, useSensor, useSensors,
    PointerSensor, KeyboardSensor, closestCenter,
} from '@dnd-kit/core';
import { landingPageAPI } from '../../services/api';
import { COMPONENT_REGISTRY, CATEGORIES, createNode, isContainerType } from './registry/componentRegistry';
import { validateNodeTree } from './registry/validate';
import { ROOT_ID, findNode, insertNode, removeNode, updateNodeProps, updateNodeStyle, updateNodeStyles, updateNodeLayers } from './treeHelpers';
import { getBackgroundLayers, backgroundStyleFor } from '../Portal/backgroundLayers';
import BackgroundLayersEditor from './panels/BackgroundLayersEditor';
import { applyDragEnd, PALETTE_PREFIX, CONTAINER_PREFIX, AFTER_PREFIX } from './dragEndLogic';
import {
    withFreePlacement, snapPosition, alignPosition, nudgeDelta, clampPercent, angleFromPoint, rotationValue,
} from './freeLayout';
import { applyEdit, undo, redo } from './undoRedo';
import { isImageType } from './imageFit';
import CommitField from './CommitField';
import NavigatorPanel from './panels/NavigatorPanel';
import StylePanel from './panels/StylePanel';
import MediaPickerModal from './panels/MediaPickerModal';
import LivePreview from './panels/LivePreview';
import { BREAKPOINTS, BREAKPOINT_LABELS, BREAKPOINT_ICONS, DEVICE_PRESETS, breakpointForWidth } from './tokens';
import FrameWidthHandle from './panels/FrameWidthHandle';
import { loginSubmitMissing, LOGIN_SUBMIT_MISSING_MESSAGE } from './loginChecks';
import './LandingBuilder.css';
import { formatTime } from '../../utils/dateFormat';

// The width the page frame takes when a size class is picked (wide is a real 1600px
// wide frame — the live preview scrolls sideways to show it on a smaller screen).
const CANVAS_WIDTH_BY_BREAKPOINT = { wide: '1600px', desktop: '100%', tablet: '768px', mobile: '480px', small: '360px' };
const AUTOSAVE_DELAY_MS = 4000;
const AUTOSAVE_RETRY_DELAY_MS = 15000;

/**
 * Builder shell. Phase 5 shipped click-to-add only; this phase (6) adds
 * @dnd-kit drag-and-drop on top without removing click-to-add (both
 * remain available — click-to-add is still the only way to add a
 * component when nothing is selected as a target container, and stays
 * the simplest path for keyboard-only users who don't want to use
 * @dnd-kit's keyboard drag mode).
 *
 * Drop target id scheme:
 *   'palette:<type>'    — a palette item being dragged (the drag SOURCE, never a drop target)
 *   'container:<id>'    — drop INTO this container, appended at the end ('container:root' = the page root)
 *   'after:<nodeId>'    — drop immediately after this existing node, within its own parent
 *
 * All actual tree mutation goes through treeHelpers.js's pure, unit-tested
 * functions (treeHelpers.test.js) — this file only translates drag events
 * into calls to those functions.
 */
export default function LandingBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [page, setPage] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [autosaveError, setAutosaveError] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    // `breakpoint` is the device being PREVIEWED (canvas width, toolbar highlight). Styles are
    // always written to the Desktop design — every other device adapts automatically — unless
    // the author switches on "Customise per device", when edits go to the previewed device.
    const [breakpoint, setBreakpoint] = useState('desktop');
    const [customizeDevices, setCustomizeDevices] = useState(false);
    // Set (px) once the page frame has been freely resized by mouse; null = follow the Desktop/Tablet/Mobile preset.
    const [canvasWidth, setCanvasWidth] = useState(null);
    const [rightTab, setRightTab] = useState('content');
    const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'
    const [activeDragId, setActiveDragId] = useState(null);
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);

    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const savingRef = useRef(false);
    const autosaveTimerRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor)
    );

    const fetchPage = useCallback(() => {
        setLoading(true);
        setLoadError(null);
        return landingPageAPI.getById(id)
            .then((res) => {
                setPage(res.data.data);
                setNodes(res.data.data.nodes || []);
                setPast([]);
                setFuture([]);
                setDirty(false);
                setAutosaveError(false);
            })
            .catch((error) => {
                setLoadError(error.response?.data?.message || 'Failed to load landing page');
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    // Browser-level exit guard (tab close/refresh/external navigation) —
    // the in-app "Back" button gets its own confirm check (handleBack)
    // since this plain BrowserRouter setup has no router-level navigation
    // blocker (that needs react-router's data router APIs).
    useEffect(() => {
        if (!dirty) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);

    // performSave is the single choke point for both manual Save and
    // autosave. It always reads nodesRef.current (never a stale closure
    // value) so a save triggered after the debounce delay still sends
    // whatever the user last typed. On failure `dirty` is left untouched —
    // the edit stays live in React state and the beforeunload guard stays
    // armed, so a dropped network request never silently loses the edit.
    const performSave = useCallback(async ({ silent }) => {
        if (savingRef.current) return;
        const check = validateNodeTree(nodesRef.current);
        if (!check.valid) {
            if (!silent) toast.error(check.errors[0]);
            return;
        }
        savingRef.current = true;
        setSaving(true);
        try {
            const res = await landingPageAPI.update(id, { nodes: nodesRef.current });
            setPage(res.data.data);
            setDirty(false);
            setAutosaveError(false);
            setLastSavedAt(new Date());
            if (!silent) toast.success('Draft saved');
        } catch (error) {
            setAutosaveError(true);
            if (!silent) toast.error(error.response?.data?.message || 'Failed to save');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [id]);

    // Debounced autosave — re-arms on every edit, fires AUTOSAVE_DELAY_MS
    // after the last one. Writes only `nodes` (the draft), never touches
    // publishedNodes — Decision #10, autosave can never leak to guests.
    useEffect(() => {
        if (!dirty) return;
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => { performSave({ silent: true }); }, AUTOSAVE_DELAY_MS);
        return () => clearTimeout(autosaveTimerRef.current);
    }, [nodes, dirty, performSave]);

    // Self-healing retry once autosave has failed — keeps trying every
    // AUTOSAVE_RETRY_DELAY_MS until it succeeds or the user saves manually.
    useEffect(() => {
        if (!autosaveError || !dirty) return;
        const retryTimer = setTimeout(() => { performSave({ silent: true }); }, AUTOSAVE_RETRY_DELAY_MS);
        return () => clearTimeout(retryTimer);
    }, [autosaveError, dirty, performSave]);

    const selectedNode = useMemo(() => findNode(nodes, selectedId), [nodes, selectedId]);

    function markDirty(newNodes) {
        const result = applyEdit({ past, nodes }, newNodes);
        setPast(result.past);
        setFuture(result.future);
        setNodes(result.nodes);
        setDirty(true);
    }

    function handleUndo() {
        const result = undo({ past, future, nodes });
        if (!result) return;
        setPast(result.past);
        setFuture(result.future);
        setNodes(result.nodes);
        setSelectedId(null);
        setDirty(true);
    }

    function handleRedo() {
        const result = redo({ past, future, nodes });
        if (!result) return;
        setPast(result.past);
        setFuture(result.future);
        setNodes(result.nodes);
        setSelectedId(null);
        setDirty(true);
    }

    useEffect(() => {
        function onKeyDown(e) {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return;
            if (!(e.ctrlKey || e.metaKey)) {
                // Arrow keys nudge the selected floating component (Shift = bigger steps).
                const delta = nudgeDelta(e.key, e.shiftKey);
                if (delta && viewMode === 'edit' && selectedNode?.style?.[editBreakpoint]?.position === 'absolute') {
                    e.preventDefault();
                    handleNudge(selectedNode.id, delta);
                }
                return;
            }
            if (e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
            else if (e.key.toLowerCase() === 'z' && e.shiftKey) { e.preventDefault(); handleRedo(); }
            else if (e.key.toLowerCase() === 'y') { e.preventDefault(); handleRedo(); }
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    });

    function handleAddComponent(type) {
        const node = createNode(type);
        const target = selectedNode && isContainerType(selectedNode.type) ? selectedNode.id : null;
        markDirty(withFreePlacement(insertNode(nodes, target, node), node.id));
        setSelectedId(node.id);
    }

    function handleDeleteNode(nodeId) {
        markDirty(removeNode(nodes, nodeId));
        if (selectedId === nodeId) setSelectedId(null);
    }

    function handleUpdateProps(nodeId, propKey, value) {
        markDirty(updateNodeProps(nodes, nodeId, propKey, value));
    }

    function handleUpdateStyle(nodeId, bp, cssProp, value) {
        markDirty(updateNodeStyle(nodes, nodeId, bp, cssProp, value));
    }

    // Picking a device preset returns the page to that preset's width.
    function selectBreakpoint(bp) {
        setBreakpoint(bp);
        setCanvasWidth(null);
    }

    // Dragging the page edge: any width, and the matching Desktop/Tablet/Mobile
    // bucket (same thresholds as the guest page's media queries) becomes the
    // one being edited/previewed.
    function handleFrameResize(px) {
        setCanvasWidth(px);
        setBreakpoint(breakpointForWidth(px));
    }

    const editBreakpoint = customizeDevices ? breakpoint : 'desktop';
    const frameWidth = canvasWidth != null ? `${canvasWidth}px` : CANVAS_WIDTH_BY_BREAKPOINT[breakpoint];

    // The selected floating component's box as % of its container, measured
    // from the real canvas (offset* ignores rotation, so it is the unrotated box).
    function measureFloating(nodeId) {
        const wrap = document.querySelector(`[data-node-id="${nodeId}"]`);
        const container = wrap?.parentElement;
        if (!wrap || !container || !container.clientWidth || !container.clientHeight) return null;
        return {
            left: (wrap.offsetLeft / container.clientWidth) * 100,
            top: (wrap.offsetTop / container.clientHeight) * 100,
            width: (wrap.offsetWidth / container.clientWidth) * 100,
            height: (wrap.offsetHeight / container.clientHeight) * 100,
        };
    }

    const pct = (n) => `${Math.round(n)}%`;

    // Align a floating component to its container (left / centre / right / top / middle / bottom).
    function handleAlign(nodeId, how) {
        const box = measureFloating(nodeId);
        if (!box) return;
        const target = alignPosition(how, box);
        const patch = {};
        if (target.left != null) Object.assign(patch, { left: pct(target.left), right: '' });
        if (target.top != null) Object.assign(patch, { top: pct(target.top), bottom: '' });
        if (Object.keys(patch).length) handleStylePatch(nodeId, editBreakpoint, patch);
    }

    // Arrow-key nudge of a floating component; stays inside its container.
    function handleNudge(nodeId, { dx, dy }) {
        const style = findNode(nodes, nodeId)?.style?.[editBreakpoint] || {};
        const box = measureFloating(nodeId);
        const left = Number.isFinite(parseFloat(style.left)) ? parseFloat(style.left) : box?.left;
        const top = Number.isFinite(parseFloat(style.top)) ? parseFloat(style.top) : box?.top;
        if (!Number.isFinite(left) || !Number.isFinite(top)) return;
        const maxLeft = box ? Math.max(0, 100 - box.width) : 100;
        const maxTop = box ? Math.max(0, 100 - box.height) : 100;
        handleStylePatch(nodeId, editBreakpoint, {
            left: pct(clampPercent(left + dx, maxLeft)),
            top: pct(clampPercent(top + dy, maxTop)),
            right: '',
            bottom: '',
        });
    }

    // Several style props as one undo step (the Style tab's Position switch).
    function handleStylePatch(nodeId, bp, patch) {
        markDirty(updateNodeStyles(nodes, nodeId, bp, patch));
    }

    // A floating component was dragged: it is now anchored by left/top, so any
    // old right/bottom anchor is dropped. One gesture = one undo step.
    function handleMoveNode(nodeId, { left, top }) {
        markDirty(updateNodeStyles(nodes, nodeId, editBreakpoint, { left, top, right: '', bottom: '' }));
    }

    function handleUpdateLayers(nodeId, layers) {
        markDirty(updateNodeLayers(nodes, nodeId, layers));
    }

    // One mouse-resize gesture = ONE undo step, even when it changes both
    // height and width (corner handle).
    function handleResizeNode(nodeId, { height, width }) {
        const node = findNode(nodes, nodeId);
        const bucket = node?.style?.[editBreakpoint] || {};
        const patch = {};
        if (height !== undefined) patch.height = height; // '' clears it (auto height)
        if (width !== undefined) patch.width = width;
        // A picture given BOTH a width and a real height would be stretched out of
        // shape — crop it to fit instead, unless the page already chose how it fills.
        if (isImageType(node?.type) && height && !bucket.objectFit) patch.objectFit = 'cover';
        markDirty(updateNodeStyles(nodes, nodeId, editBreakpoint, patch));
    }

    // The registry entry of whatever is being dragged (a palette item or an
    // existing node) — drives the floating chip that follows the cursor.
    function dragDef(dragId) {
        if (!dragId) return null;
        if (dragId.startsWith(PALETTE_PREFIX)) return COMPONENT_REGISTRY[dragId.slice(PALETTE_PREFIX.length)];
        const node = findNode(nodes, dragId);
        return node ? COMPONENT_REGISTRY[node.type] : null;
    }

    function handleDragEnd({ active, over }) {
        setActiveDragId(null);
        if (!over) return;
        const result = applyDragEnd(nodes, String(active.id), String(over.id));
        switch (result.type) {
            case 'inserted':
                markDirty(withFreePlacement(result.nodes, result.newNodeId));
                setSelectedId(result.newNodeId);
                break;
            case 'moved':
                markDirty(withFreePlacement(result.nodes, String(active.id)));
                break;
            case 'rejected':
                toast.warning(result.reason);
                break;
            default:
            // 'noop' — nothing to do
        }
    }

    async function handleSaveClick() {
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        await performSave({ silent: false });
    }

    async function handlePublish() {
        if (dirty || saving) {
            toast.warning('Save your draft before publishing');
            return;
        }
        if (loginSubmitMissing(nodes)) {
            toast.error(LOGIN_SUBMIT_MISSING_MESSAGE);
            return;
        }
        try {
            await landingPageAPI.publish(id);
            setPage((p) => ({ ...p, status: 'published' }));
            toast.success('Published');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to publish');
        }
    }

    async function handleUnpublish() {
        try {
            await landingPageAPI.unpublish(id);
            setPage((p) => ({ ...p, status: 'draft' }));
            toast.success('Unpublished');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to unpublish');
        }
    }

    function handleBack() {
        if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
            return;
        }
        navigate('/landing-pages');
    }

    if (loading) {
        return <div className="loading-modern"><div className="spinner-modern"></div><p>Loading builder...</p></div>;
    }
    if (loadError) {
        return (
            <div className="empty-state-modern">
                <div className="empty-icon"><i className="fas fa-exclamation-triangle"></i></div>
                <h3>Couldn't load this landing page</h3>
                <p>{loadError}</p>
                <button className="btn-primary-modern" onClick={fetchPage}>
                    <i className="fas fa-redo"></i> Retry
                </button>
            </div>
        );
    }
    if (!page) {
        return <div className="empty-state-modern"><h3>Landing page not found</h3></div>;
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveDragId(String(active.id))}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
        >
            <DragOverlay dropAnimation={null}>
                {dragDef(activeDragId) && (
                    <div className="lb-drag-ghost">
                        <i className={`fas ${dragDef(activeDragId).icon}`}></i> {dragDef(activeDragId).label}
                    </div>
                )}
            </DragOverlay>
            <div className="lb-shell">
                <div className="lb-toolbar">
                    <button className="lb-back" onClick={handleBack}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <div className="lb-title-block">
                        <span className="lb-title">{page.title}</span>
                        <span className={`status-badge ${page.status === 'published' ? 'active' : 'inactive'}`}>{page.status}</span>
                        {saving ? (
                            <span className="lb-autosave-status saving"><i className="fas fa-spinner fa-spin"></i> Saving...</span>
                        ) : autosaveError ? (
                            <span className="lb-autosave-status error"><i className="fas fa-exclamation-triangle"></i> Autosave failed — retrying</span>
                        ) : dirty ? (
                            <span className="lb-unsaved">Unsaved changes</span>
                        ) : lastSavedAt ? (
                            <span className="lb-autosave-status saved"><i className="fas fa-check"></i> Saved {formatTime(lastSavedAt)}</span>
                        ) : null}
                    </div>
                    <div className="lb-view-toggle">
                        <button type="button" className={`lb-view-btn ${viewMode === 'edit' ? 'active' : ''}`} onClick={() => setViewMode('edit')}>
                            <i className="fas fa-pen"></i> Edit
                        </button>
                        <button type="button" className={`lb-view-btn ${viewMode === 'preview' ? 'active' : ''}`} onClick={() => setViewMode('preview')}>
                            <i className="fas fa-eye"></i> Live Preview
                        </button>
                    </div>
                    <div className="lb-history-controls">
                        <button className="lb-history-btn" onClick={handleUndo} disabled={past.length === 0} title="Undo (Ctrl+Z)">
                            <i className="fas fa-undo"></i>
                        </button>
                        <button className="lb-history-btn" onClick={handleRedo} disabled={future.length === 0} title="Redo (Ctrl+Shift+Z)">
                            <i className="fas fa-redo"></i>
                        </button>
                    </div>
                    <div className="lb-device-toggle">
                        {BREAKPOINTS.map((bp) => (
                            <button
                                key={bp}
                                type="button"
                                className={`lb-device-btn ${breakpoint === bp ? 'active' : ''}`}
                                onClick={() => selectBreakpoint(bp)}
                                title={BREAKPOINT_LABELS[bp]}
                                aria-label={BREAKPOINT_LABELS[bp]}
                            >
                                <i className={`fas ${BREAKPOINT_ICONS[bp]}`}></i>
                            </button>
                        ))}
                        <select
                            className="lb-device-select"
                            aria-label="Preview on a device"
                            value=""
                            onChange={(e) => { if (e.target.value) handleFrameResize(Number(e.target.value)); }}
                        >
                            <option value="">Device…</option>
                            {DEVICE_PRESETS.map((d) => <option key={d.label} value={d.width}>{d.label} ({d.width}px)</option>)}
                        </select>
                    </div>
                    <label className="lb-customize-toggle" title="Off: design once for Desktop and every other device adapts automatically. On: set styles for the device you are previewing.">
                        <input type="checkbox" checked={customizeDevices} onChange={(e) => setCustomizeDevices(e.target.checked)} />
                        Customise per device
                    </label>
                    {loginSubmitMissing(nodes) && (
                        <div className="lb-warn-chip" role="alert">
                            <i className="fas fa-exclamation-triangle"></i>
                            <span>Login form has no Terms + Connect button</span>
                            <button type="button" onClick={() => handleAddComponent('login-submit')}>Add it</button>
                        </div>
                    )}
                    <div className="lb-toolbar-actions">
                        <button className="btn-secondary-modern" onClick={handleSaveClick} disabled={saving}>
                            <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save Draft'}
                        </button>
                        {page.status === 'published' ? (
                            <button className="btn-secondary-modern" onClick={handleUnpublish}>
                                <i className="fas fa-eye-slash"></i> Unpublish
                            </button>
                        ) : (
                            <button className="btn-primary-modern" onClick={handlePublish}>
                                <i className="fas fa-globe"></i> Publish
                            </button>
                        )}
                    </div>
                </div>

                {viewMode === 'preview' ? (
                    <LivePreview
                        nodes={nodes}
                        frameWidth={frameWidth}
                        onWidthChange={handleFrameResize}
                    />
                ) : (
                <div className="lb-body">
                    <PalettePanel onAdd={handleAddComponent} />

                    <div className="lb-canvas-wrap">
                        <RootDropZone nodes={nodes}>
                            <div className="lb-canvas lb-frame" style={{ maxWidth: frameWidth }}>
                                {nodes.length === 0 ? (
                                    <div className="lb-canvas-empty">
                                        <i className="fas fa-mouse-pointer"></i>
                                        <p>Click or drag a component from the left to add it here.</p>
                                    </div>
                                ) : (
                                    nodes.map((node) => (
                                        <CanvasNode
                                            key={node.id}
                                            node={node}
                                            breakpoint={editBreakpoint}
                                            selectedId={selectedId}
                                            onSelect={setSelectedId}
                                            onDelete={handleDeleteNode}
                                            onResize={handleResizeNode}
                                            onMoveNode={handleMoveNode}
                                            onStylePatchNode={(nodeId, patch) => handleStylePatch(nodeId, editBreakpoint, patch)}
                                        />
                                    ))
                                )}
                                <FrameWidthHandle onWidthChange={handleFrameResize} />
                            </div>
                        </RootDropZone>
                    </div>

                    <div className="lb-right-col">
                        <NavigatorPanel nodes={nodes} selectedId={selectedId} onSelect={setSelectedId} />
                        <div className="lb-right-tabs">
                            <button className={`lb-right-tab ${rightTab === 'content' ? 'active' : ''}`} onClick={() => setRightTab('content')}>Content</button>
                            <button className={`lb-right-tab ${rightTab === 'style' ? 'active' : ''}`} onClick={() => setRightTab('style')}>Style</button>
                        </div>
                        {rightTab === 'content' ? (
                            <InspectorPanel node={selectedNode} onChange={handleUpdateProps} onLayersChange={handleUpdateLayers} tenantId={page.tenantId} />
                        ) : (
                            <StylePanel node={selectedNode} breakpoint={editBreakpoint} showDeviceTabs={customizeDevices} onBreakpointChange={selectBreakpoint} onStyleChange={handleUpdateStyle} onStylePatch={handleStylePatch} onAlign={handleAlign} />
                        )}
                    </div>
                </div>
                )}
            </div>
        </DndContext>
    );
}

// ── Palette (draggable sources) ─────────────────────────────────────
function PalettePanel({ onAdd }) {
    const [activeCategory, setActiveCategory] = useState('layout');
    const items = Object.entries(COMPONENT_REGISTRY).filter(([, def]) => def.category === activeCategory);

    return (
        <div className="lb-palette">
            <div className="lb-palette-tabs">
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat}
                        className={`lb-palette-tab ${activeCategory === cat ? 'active' : ''}`}
                        onClick={() => setActiveCategory(cat)}
                    >
                        {cat === 'hotwifi' ? 'HotWiFi' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </button>
                ))}
            </div>
            <div className="lb-palette-list">
                {items.map(([type, def]) => (
                    <PaletteItem key={type} type={type} def={def} onAdd={onAdd} />
                ))}
            </div>
        </div>
    );
}

function PaletteItem({ type, def, onAdd }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: PALETTE_PREFIX + type });
    return (
        <button
            ref={setNodeRef}
            className={`lb-palette-item ${isDragging ? 'dragging' : ''}`}
            onClick={() => onAdd(type)}
            title={`Add ${def.label} (click, or drag onto the canvas)`}
            {...listeners}
            {...attributes}
        >
            <i className={`fas ${def.icon}`}></i>
            <span>{def.label}</span>
        </button>
    );
}

// ── Root drop zone (dropping directly onto empty canvas background) ──
function RootDropZone({ children }) {
    const { setNodeRef, isOver } = useDroppable({ id: CONTAINER_PREFIX + ROOT_ID });
    return <div ref={setNodeRef} className={isOver ? 'lb-drop-over' : ''}>{children}</div>;
}

// ── Canvas (editor-mode tree renderer — separate from the guest renderer) ──
const MIN_RESIZE_HEIGHT = 24;

// Style keys that place a floating component inside its container.
const POSITION_KEYS = ['position', 'top', 'right', 'bottom', 'left', 'width', 'zIndex', 'transform'];

function CanvasNode({ node, breakpoint, selectedId, onSelect, onDelete, onResize, onMoveNode, onStylePatchNode }) {
    const def = COMPONENT_REGISTRY[node.type];
    const isSelected = selectedId === node.id;
    const sizeTargetRef = useRef(null); // the box whose height/width the handles change
    const wrapRef = useRef(null); // the component's outer box — what moves when it is floating
    const [liveMove, setLiveMove] = useState(null); // { left, top } (%) while a floating component is being dragged
    const [liveRotate, setLiveRotate] = useState(null); // degrees while the rotate handle is being dragged
    const [guides, setGuides] = useState(null); // snap guide lines (screen px) while dragging
    const [liveSize, setLiveSize] = useState(null); // { height?: px, width?: % } while dragging a handle
    const isImageNode = isImageType(node.type);
    // Live preview of the CURRENTLY-SELECTED breakpoint only — the builder
    // canvas is a fixed simulated-width area, not a real responsive
    // viewport, so it just shows one breakpoint's styles at a time
    // (unlike the guest renderer's real @media-query CSS in
    // landingPageStyles.js, which must adapt to any real device width).
    const styleForBreakpoint = node.style?.[breakpoint] || {};

    // A container's style (background colour, height, padding, text colour…)
    // and a Background node's image belong on the box that actually wraps
    // its children — otherwise the canvas shows an unstyled empty box while
    // the guest page looks completely different. The image is spread last so
    // it wins over a gradient token, same as the guest renderer's inline style.
    const hasChildren = (node.children || []).length > 0;
    const hasExplicitHeight = liveSize?.height != null || !!styleForBreakpoint.height;
    const bgLayers = node.type === 'background' ? getBackgroundLayers(node) : [];
    const backgroundImage = bgLayers.length > 0
        ? {
            ...backgroundStyleFor(bgLayers),
            ...(hasChildren || hasExplicitHeight ? {} : { minHeight: 140 }),
        }
        : {};
    // While a resize handle is being dragged, show the new size immediately
    // (nothing is committed to the page — or the undo history — until release).
    // A "floating" component (Style tab → Position) is placed by
    // left/top/right/bottom inside its container instead of taking part in the
    // page flow. Its OUTER box is what moves and gets the width; the inner box
    // keeps height / colours / etc. — on the guest page these are one element.
    const isFloating = styleForBreakpoint.position === 'absolute';
    const innerBaseStyle = isFloating
        ? Object.fromEntries(Object.entries(styleForBreakpoint).filter(([key]) => !POSITION_KEYS.includes(key)))
        : styleForBreakpoint;
    const liveInnerStyle = {
        ...(liveSize?.height != null ? { height: `${liveSize.height}px` } : {}),
        ...(!isFloating && liveSize?.width != null ? { width: `${liveSize.width}%` } : {}),
    };
    const wrapStyle = isFloating
        ? {
            position: 'absolute',
            zIndex: styleForBreakpoint.zIndex ?? 3,
            transform: liveRotate != null ? (rotationValue(liveRotate) || undefined) : styleForBreakpoint.transform,
            top: styleForBreakpoint.top,
            right: styleForBreakpoint.right,
            bottom: styleForBreakpoint.bottom,
            left: styleForBreakpoint.left,
            width: liveSize?.width != null ? `${liveSize.width}%` : styleForBreakpoint.width,
            ...(liveMove ? { left: `${liveMove.left}%`, top: `${liveMove.top}%`, right: 'auto', bottom: 'auto' } : {}),
        }
        : undefined;
    const childrenBoxStyle = def?.isContainer ? { ...innerBaseStyle, ...backgroundImage, ...liveInnerStyle } : undefined;
    const previewStyle = def?.isContainer ? undefined : { ...innerBaseStyle, ...liveInnerStyle };

    // Mouse move (floating components only): drag the handle anywhere inside
    // the container. Position is stored as left/top in % of the container, so
    // it scales with the page; the component can't be dragged out of its box.
    function startMove(e) {
        if (e.target.closest && e.target.closest('button')) return; // the delete button is not a drag surface
        e.preventDefault();
        e.stopPropagation();
        const wrap = wrapRef.current;
        const container = wrap?.parentElement;
        if (!wrap || !container) return;
        const startX = e.clientX;
        const startY = e.clientY;
        const wrapRect = wrap.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width || 1;
        const containerHeight = containerRect.height || 1;
        const startLeft = wrapRect.left - containerRect.left;
        const startTop = wrapRect.top - containerRect.top;
        const widthPct = (wrapRect.width / containerWidth) * 100;
        const heightPct = (wrapRect.height / containerHeight) * 100;
        const maxLeft = Math.max(0, 100 - widthPct);
        const maxTop = Math.max(0, 100 - heightPct);
        let latest = null;

        const handleMove = (ev) => {
            const rawLeft = clampPercent(((startLeft + ev.clientX - startX) / containerWidth) * 100, maxLeft);
            const rawTop = clampPercent(((startTop + ev.clientY - startY) / containerHeight) * 100, maxTop);
            // Snap to the container's edges and centre lines (hold Alt to move freely).
            const snapped = ev.altKey
                ? { left: rawLeft, top: rawTop, guideX: null, guideY: null }
                : snapPosition({ left: rawLeft, top: rawTop, width: widthPct, height: heightPct });
            latest = { left: Math.round(snapped.left), top: Math.round(snapped.top) };
            setLiveMove(latest);
            const at = { start: 0, center: 0.5, end: 1 };
            setGuides({
                x: snapped.guideX ? containerRect.left + containerWidth * at[snapped.guideX] : null,
                y: snapped.guideY ? containerRect.top + containerHeight * at[snapped.guideY] : null,
                rect: containerRect,
            });
        };
        const handleUp = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
            setLiveMove(null);
            setGuides(null);
            if (latest) onMoveNode(node.id, { left: `${latest.left}%`, top: `${latest.top}%` });
        };
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
    }

    // Rotate: drag the round handle above the component; it turns around its
    // own centre and snaps to every 15 degrees (hold Alt for free rotation).
    function startRotate(e) {
        e.preventDefault();
        e.stopPropagation();
        const wrap = wrapRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        let latest = null;

        const handleRotate = (ev) => {
            latest = angleFromPoint(cx, cy, ev.clientX, ev.clientY, { snap: !ev.altKey });
            setLiveRotate(latest);
        };
        const handleUp = () => {
            window.removeEventListener('pointermove', handleRotate);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
            setLiveRotate(null);
            if (latest != null) onStylePatchNode(node.id, { transform: rotationValue(latest) });
        };
        window.addEventListener('pointermove', handleRotate);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
    }

    // Mouse resize: bottom edge = height (px), right edge = width (% of the
    // parent, so the result stays responsive), corner = both. Written to the
    // CURRENT breakpoint, exactly like the Style tab's Width/Height fields.
    function startResize(e, axis) {
        e.preventDefault();
        e.stopPropagation();
        const heightEl = sizeTargetRef.current;
        if (!heightEl) return;
        // A floating component's width lives on its outer box (the one that moves).
        const widthEl = isFloating && wrapRef.current ? wrapRef.current : heightEl;
        const startX = e.clientX;
        const startY = e.clientY;
        const heightRect = heightEl.getBoundingClientRect();
        const widthRect = widthEl.getBoundingClientRect();
        const parentWidth = widthEl.parentElement?.getBoundingClientRect().width || widthRect.width || 1;
        let latest = null;

        const onResizeMove = (ev) => {
            // The corner handle on a picture keeps its proportions (width only, height
            // follows on its own); hold Shift to change width and height separately.
            const lockRatio = isImageNode && axis === 'xy' && !ev.shiftKey;
            const next = { lockRatio };
            if (axis !== 'x' && !lockRatio) next.height = Math.max(MIN_RESIZE_HEIGHT, Math.round(heightRect.height + ev.clientY - startY));
            if (axis !== 'y') next.width = Math.min(100, Math.max(10, Math.round(((widthRect.width + ev.clientX - startX) / parentWidth) * 100)));
            latest = next;
            setLiveSize(next);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onResizeMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            setLiveSize(null);
            if (latest) {
                onResize(node.id, {
                    height: latest.height != null ? `${latest.height}px` : (latest.lockRatio ? '' : undefined),
                    width: latest.width != null ? `${latest.width}%` : undefined,
                });
            }
        };
        window.addEventListener('pointermove', onResizeMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }

    const resizeHandles = isSelected && (
        <>
            <span className="lb-resize-handle lb-resize-s" title="Drag to change height" onPointerDown={(e) => startResize(e, 'y')} onClick={(e) => e.stopPropagation()} />
            <span className="lb-resize-handle lb-resize-e" title="Drag to change width" onPointerDown={(e) => startResize(e, 'x')} onClick={(e) => e.stopPropagation()} />
            <span className="lb-resize-handle lb-resize-se" title="Drag to change width and height" onPointerDown={(e) => startResize(e, 'xy')} onClick={(e) => e.stopPropagation()} />
        </>
    );

    const drag = useDraggable({ id: node.id });
    const dropAfter = useDroppable({ id: AFTER_PREFIX + node.id });
    const dropInto = useDroppable({ id: CONTAINER_PREFIX + node.id });

    return (
        <div
            ref={(el) => { dropAfter.setNodeRef(el); wrapRef.current = el; }}
            className={`lb-node-wrap ${isFloating ? 'lb-floating' : ''} ${dropAfter.isOver ? 'lb-drop-over' : ''}`}
            style={wrapStyle}
            data-node-id={node.id}
        >
            {guides && guides.x != null && <span className="lb-snap-guide lb-snap-v" style={{ left: guides.x, top: guides.rect.top, height: guides.rect.height }} />}
            {guides && guides.y != null && <span className="lb-snap-guide lb-snap-h" style={{ top: guides.y, left: guides.rect.left, width: guides.rect.width }} />}
            {isSelected && isFloating && (
                <span
                    className="lb-move-handle"
                    title="Drag to place it anywhere"
                    onPointerDown={startMove}
                    onClick={(e) => e.stopPropagation()}
                >
                    <i className="fas fa-arrows-alt"></i>
                </span>
            )}
            {isSelected && isFloating && (
                <span
                    className="lb-rotate-handle"
                    title="Drag to rotate (hold Alt for free rotation)"
                    onPointerDown={startRotate}
                    onClick={(e) => e.stopPropagation()}
                >
                    <i className="fas fa-sync-alt"></i>
                </span>
            )}
            <div
                ref={drag.setNodeRef}
                className={`lb-node ${isSelected ? 'selected' : ''} ${def?.isContainer ? 'lb-node-container' : ''} ${drag.isDragging ? 'dragging' : ''}`}
                onClick={(e) => { e.stopPropagation(); onSelect(node.id); }}
            >
                {/* The whole label bar is the drag handle (not just the tiny grip),
                    so a component can be grabbed anywhere along its title. */}
                <div
                    className="lb-node-label"
                    title={isFloating ? 'Drag to place it anywhere' : 'Drag to move'}
                    ref={drag.setActivatorNodeRef}
                    {...(isFloating ? { onPointerDown: startMove } : drag.listeners)}
                    {...drag.attributes}
                >
                    <span className="lb-node-handle">
                        <i className="fas fa-grip-vertical"></i>
                    </span>
                    <i className={`fas ${def?.icon || 'fa-cube'}`}></i>
                    <span>{def?.label || node.type}</span>
                    <button className="lb-node-delete" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(node.id); }}>
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
                <div
                    ref={def?.isContainer ? undefined : sizeTargetRef}
                    className={`lb-node-preview ${def?.isContainer ? '' : 'lb-resizable'}`}
                    style={previewStyle}
                    onPointerDown={isFloating && !def?.isContainer ? startMove : undefined}
                >
                    <NodePreview node={node} style={innerBaseStyle} />
                    {!def?.isContainer && resizeHandles}
                </div>
                {def?.isContainer && (
                    <div
                        ref={(el) => { dropInto.setNodeRef(el); sizeTargetRef.current = el; }}
                        className={`lb-node-children lb-resizable ${dropInto.isOver ? 'lb-drop-over' : ''}`}
                        style={childrenBoxStyle}
                    >
                        {(node.children || []).length === 0 ? (
                            <div className="lb-node-empty-slot">Empty — click or drag a component here</div>
                        ) : (
                            node.children.map((child) => (
                                <CanvasNode key={child.id} node={child} breakpoint={breakpoint} selectedId={selectedId} onSelect={onSelect} onDelete={onDelete} onResize={onResize} onMoveNode={onMoveNode} onStylePatchNode={onStylePatchNode} />
                            ))
                        )}
                        {resizeHandles}
                    </div>
                )}
            </div>
        </div>
    );
}

// Lightweight, non-functional preview of what a node roughly looks like —
// intentionally simple; the true rendering lives in LandingPageRenderer.jsx
// (used both for guest rendering and, via /preview, the "real" preview).
function NodePreview({ node, style = {} }) {
    const { type, props = {} } = node;
    // Pictures show as they will on the page: sized by their box, cropped / positioned
    // per the fit and focus, never stretched unless "stretch" was chosen.
    const pictureStyle = {
        display: 'block',
        maxWidth: '100%',
        width: style.width ? '100%' : (type === 'logo' ? 160 : 'auto'),
        height: style.height ? '100%' : 'auto',
        objectFit: style.objectFit || undefined,
        objectPosition: style.objectPosition || undefined,
        borderRadius: style.borderRadius || undefined,
    };
    switch (type) {
        case 'heading': return <strong>{props.text || 'Heading'}</strong>;
        case 'paragraph': return <p style={{ margin: 0 }}>{props.text || 'Paragraph text'}</p>;
        case 'button': return <button type="button" disabled>{props.text || 'Button'}</button>;
        case 'image': return props.src ? <img src={props.src} alt="" style={pictureStyle} /> : <span className="lb-placeholder">No image selected</span>;
        case 'logo': return props.src ? <img src={props.src} alt="" style={pictureStyle} /> : <span className="lb-placeholder">Logo placeholder (uses the branch logo if left empty)</span>;
        case 'otp-login': return <span className="lb-placeholder">OTP Login Form (renders live on the guest portal)</span>;
        case 'pms-login': return <span className="lb-placeholder">PMS Guest Login Form (renders live on the guest portal)</span>;
        case 'voucher-login': return <span className="lb-placeholder">Voucher Login Form (renders live on the guest portal)</span>;
        case 'auth-method-selector': return <span className="lb-placeholder">Authentication Method Selector (shows enabled methods as tabs)</span>;
        case 'resident-login': return <span className="lb-placeholder">Resident Login Form (renders live on the guest portal)</span>;
        case 'staff-login': return <span className="lb-placeholder">Staff Login Form (renders live on the guest portal)</span>;
        case 'login-methods': return <span className="lb-placeholder">All Login Methods — a tab per enabled method plus its form (see Live Preview)</span>;
        case 'free-access-button': return <button type="button" disabled>{props.text || 'Continue with Free Wi-Fi'}</button>;
        case 'qr-code': return <span className="lb-placeholder">QR Code</span>;
        case 'richtext': return <span>{props.text || 'Rich text'}</span>;
        case 'icon': return <i className={`fas ${props.icon || 'fa-star'}`}></i>;
        case 'divider': return <hr style={{ margin: '4px 0' }} />;
        case 'spacer': return <span className="lb-placeholder">Spacer ({props.height || '24px'})</span>;
        case 'video': return <span className="lb-placeholder">{props.src ? 'Video selected' : 'No video selected'}</span>;
        case 'terms-conditions': return <span>{props.text || 'Terms & Conditions (from the branch settings)'}</span>;
        case 'privacy-policy': return <span>{props.text || 'Privacy policy text'}</span>;
        case 'contact-info': return <span>{props.text || 'Contact information'}</span>;
        case 'hotel-info': return <span className="lb-placeholder">Hotel name (from the branch)</span>;
        case 'guest-welcome': return <span className="lb-placeholder">Welcome message (from the branch)</span>;
        case 'scenic-portal': return (
            <span className="lb-placeholder">
                Scenic Sign-in Page — {props.heroTitle || 'branch name'} · photo, logo, EN / हिं / मरा and WhatsApp / SMS sign-in (see Live Preview)
            </span>
        );
        default: return null;
    }
}

// ── Inspector ────────────────────────────────────────────────────────
function InspectorPanel({ node, onChange, onLayersChange, tenantId }) {
    const [pickerField, setPickerField] = useState(null); // field.key currently being picked, or null

    if (!node) {
        return (
            <div className="lb-inspector">
                <p className="lb-inspector-empty">Select a component to edit its properties.</p>
            </div>
        );
    }
    const def = COMPONENT_REGISTRY[node.type];

    return (
        <div className="lb-inspector">
            <h4>{def?.label || node.type}</h4>
            {(def?.propFields || []).length === 0 && node.type !== 'background' && (
                <p className="lb-inspector-empty">This component has no editable properties yet.</p>
            )}
            {node.type === 'background' && (
                <BackgroundLayersEditor node={node} tenantId={tenantId} onChange={(layers) => onLayersChange(node.id, layers)} />
            )}
            {(def?.propFields || []).map((field) => (
                <div className="form-group" key={field.key}>
                    <label>{field.label}</label>
                    {field.type === 'textarea' ? (
                        <CommitField
                            multiline
                            rows={field.rows}
                            maxLength={field.maxLength}
                            maxWords={field.maxWords}
                            placeholder={field.placeholder}
                            value={node.props?.[field.key] || ''}
                            onCommit={(v) => onChange(node.id, field.key, v)}
                        />
                    ) : field.type === 'token' ? (
                        <TokenSelect
                            tokens={field.tokens}
                            value={node.props?.[field.key] || ''}
                            onChange={(v) => onChange(node.id, field.key, v)}
                        />
                    ) : field.type === 'select' ? (
                        <select
                            className="form-control"
                            value={node.props?.[field.key] || ''}
                            onChange={(e) => onChange(node.id, field.key, e.target.value)}
                        >
                            {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    ) : field.type === 'media' ? (
                        <div className="lb-media-field">
                            {node.props?.[field.key] && (
                                <img src={node.props[field.key]} alt="" className="lb-media-field-thumb" />
                            )}
                            <button type="button" className="btn-secondary-modern" onClick={() => setPickerField(field.key)}>
                                <i className="fas fa-image"></i> {node.props?.[field.key] ? 'Change' : 'Choose'} Image
                            </button>
                            {node.props?.[field.key] && (
                                <button type="button" className="lb-media-field-clear" title="Remove" onClick={() => onChange(node.id, field.key, '')}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    ) : (
                        <CommitField
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={node.props?.[field.key] ?? ''}
                            onCommit={(v) => onChange(node.id, field.key, v)}
                        />
                    )}
                </div>
            ))}
            <MediaPickerModal
                open={!!pickerField}
                mediaType={node.type === 'video' ? 'video' : 'image'}
                tenantId={tenantId}
                onSelect={(url) => onChange(node.id, pickerField, url)}
                onClose={() => setPickerField(null)}
            />
        </div>
    );
}

// A pick-from-the-palette field (colours, radii). "Default" writes an empty
// value, which the renderer ignores — the component keeps its normal look. A
// value that isn't in the palette (e.g. one set by a template) is shown as
// "Custom" instead of silently displaying the wrong option.
function TokenSelect({ tokens, value, onChange }) {
    const isCustom = value !== '' && !tokens.some((t) => t.value === value);
    return (
        <select className="form-control" value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="">Default</option>
            {isCustom && <option value={value}>Custom ({value})</option>}
            {tokens.map((t) => <option key={t.label} value={t.value}>{t.label}</option>)}
        </select>
    );
}
